import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const TestimonialsSlider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const { ref, inView } = useInView({
    threshold: 0.2,
    triggerOnce: false
  });

  const testimonials = [
    {
      id: 1,
      name: "Prabhu Natarajan",
      role: "Business Owner",
      company: "MaxyWalk",
      avatar: "👩‍💼",
      rating: 4,
      testimonial: "The e-commerce site you built for our shoe store has been a game changer. It’s fast, clean, and easy for customers to browse and buy. We’ve seen a noticeable improvement in engagement and sales",
      project: "E-Commerce Shoe Store",
      gradient: "from-blue-500 to-cyan-500",
      bgGradient: "from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20",
      borderColor: "border-blue-200 dark:border-blue-700"
    },
    {
      id: 2,
      name: "A.J.F. Prabakaran",
      role: "Principal",
      company: "Bishop Corrie Anglo Indian HSS",
      avatar: "👨‍💻",
      rating: 5,
      testimonial: "Working with you on the Mayzo platform was seamless. The end product is fast, user-friendly, and perfectly aligns with our business goals. The real-time features and clean interface have greatly enhanced our customer experience.",
      project: "School Website",
      gradient: "from-purple-500 to-pink-500",
      bgGradient: "from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20",
      borderColor: "border-purple-200 dark:border-purple-700"
    },
    {
      id: 3,
      name: "Olivia Vasanthi",
      role: "Managing Director",
      company: "Mayzo Grocery Delivery",
      avatar: "👩‍🚀",
      rating: 4,
      testimonial: "The team at Tech4All doesn't just build websites, they craft digital experiences. Our user engagement increased by 25% and conversion rates doubled. Their strategic approach is unmatched.",
      project: "Grocery & Food Delivery Portfolio",
      gradient: "from-emerald-500 to-teal-500",
      bgGradient: "from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20",
      borderColor: "border-emerald-200 dark:border-emerald-700"
    },
    {
      id: 4,
      name: "Naga Vishnu",
      role: "CEO",
      company: "Navi Promotions",
      avatar: "👨‍🎯",
      rating: 5,
      testimonial: "Our new website presents our products and services with clarity and professionalism. The layout, product display, and brochure access features have significantly improved how we engage with clients and partners online.",
      project: "Semi Conductor Products Portfolio",
      gradient: "from-orange-500 to-red-500",
      bgGradient: "from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20",
      borderColor: "border-orange-200 dark:border-orange-700"
    }
  ];

  // Auto-advance testimonials
  useEffect(() => {
    if (!inView) return;
    
    const interval = setInterval(() => {
      setDirection(1);
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    
    return () => clearInterval(interval);
  }, [inView, testimonials.length]);

  const goToSlide = (index) => {
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
  };

  const nextSlide = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
      scale: 0.8,
      rotateY: direction > 0 ? 45 : -45
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      scale: 1,
      rotateY: 0
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0,
      scale: 0.8,
      rotateY: direction < 0 ? 45 : -45
    })
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section className="relative py-8 sm:py-16 lg:py-20 overflow-hidden bg-gradient-to-br from-slate-50 via-purple-50/30 to-blue-50/20 dark:from-slate-900 dark:via-purple-900/10 dark:to-blue-900/5" ref={ref}>
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Floating Orbs */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className={`absolute w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gradient-to-r ${testimonials[i % testimonials.length].gradient} opacity-5`}
            animate={{
              x: [0, 100, 0],
              y: [0, -50, 0],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 8 + i * 2,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 1.5
            }}
            style={{
              left: `${10 + i * 15}%`,
              top: `${20 + (i % 3) * 20}%`,
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-8 sm:mb-12 lg:mb-16 relative z-10"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-2xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold mb-2 sm:mb-4">
            <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              What Our Clients Say
            </span>
          </h2>
          <p className="text-sm sm:text-base lg:text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto px-4">
            Real feedback from amazing clients who trusted us with their digital transformation
          </p>
        </motion.div>

        {/* Main Testimonial Display */}
        <div className="max-w-6xl mx-auto px-4 relative">
          <div className="relative min-h-[200px] sm:min-h-[300px] md:min-h-[400px] flex items-center">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="w-full"
              >
                <div className={`relative p-4 sm:p-6 md:p-8 lg:p-12 rounded-xl sm:rounded-2xl lg:rounded-3xl ${currentTestimonial.bgGradient} ${currentTestimonial.borderColor} border-2 shadow-2xl backdrop-blur-lg`}>
                  {/* Quote Icon */}
                  <div className="absolute -top-2 -left-2 sm:-top-4 sm:-left-4 w-8 h-8 sm:w-12 sm:h-12 md:w-16 md:h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-white text-lg sm:text-2xl md:text-3xl font-bold">"</span>
                  </div>
                  
                  <div className="flex flex-col md:flex-row md:items-center gap-4 sm:gap-6 md:gap-8">
                    {/* Client Info */}
                    <div className="flex-shrink-0 text-center md:text-left">
                      <div className="inline-block w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 flex items-center justify-center text-2xl sm:text-3xl md:text-4xl mb-2 sm:mb-3 md:mb-4 mx-auto md:mx-0 shadow-lg">
                        {currentTestimonial.avatar}
                      </div>
                      
                      <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-slate-800 dark:text-slate-100 mb-1">
                        {currentTestimonial.name}
                      </h3>
                      <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 mb-1 sm:mb-2">
                        {currentTestimonial.role}
                      </p>
                      <p className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400 mb-2 sm:mb-3">
                        {currentTestimonial.company}
                      </p>
                      
                      {/* Rating */}
                      <div className="flex justify-center md:justify-start gap-0.5 sm:gap-1 mb-2 sm:mb-3">
                        {[...Array(currentTestimonial.rating)].map((_, i) => (
                          <motion.span
                            key={i}
                            className="text-yellow-400 text-sm sm:text-base"
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: i * 0.1 }}
                          >
                            ⭐
                          </motion.span>
                        ))}
                      </div>
                      
                      <div className={`inline-block text-xs px-2 py-1 sm:px-3 sm:py-1 rounded-full bg-gradient-to-r ${currentTestimonial.gradient} text-white font-medium`}>
                        {currentTestimonial.project}
                      </div>
                    </div>
                    
                    {/* Testimonial Content */}
                    <div className="flex-1 flex items-center mt-2 sm:mt-4 md:mt-0 min-w-0">
                      <blockquote className="text-xs sm:text-sm md:text-lg lg:text-xl text-slate-700 dark:text-slate-200 leading-relaxed italic break-words w-full">
                        "{currentTestimonial.testimonial}"
                      </blockquote>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Dots Navigation */}
        <div className="flex justify-center gap-1 sm:gap-2 md:gap-3 mt-6 sm:mt-8 md:mt-12">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`relative w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? `bg-gradient-to-r ${currentTestimonial.gradient} scale-125`
                  : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
              }`}
              style={{ touchAction: 'manipulation' }}
            >
              {index === currentIndex && (
                <motion.div
                  className={`absolute inset-0 rounded-full bg-gradient-to-r ${currentTestimonial.gradient} opacity-30`}
                  animate={{ scale: [1, 1.5, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              )}
            </button>
          ))}
        </div>

        {/* Stats Section */}
        {/* <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 md:gap-6 mt-8 sm:mt-12 md:mt-16 max-w-4xl mx-auto px-1 sm:px-4"
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          {[
            { number: "50+", label: "Happy Clients", icon: "😊" },
            { number: "100+", label: "Projects Delivered", icon: "🚀" },
            { number: "99%", label: "Client Satisfaction", icon: "⭐" },
            { number: "24/7", label: "Support Available", icon: "🔧" }
          ].map((stat, index) => (
            <motion.div
              key={index}
              className="text-center p-2 sm:p-4 md:p-6 rounded-lg md:rounded-2xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-lg border border-gray-200 dark:border-gray-700"
              whileHover={{ scale: 1.05, y: -5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <div className="text-base sm:text-xl md:text-2xl lg:text-3xl mb-1 sm:mb-2">{stat.icon}</div>
              <div className="text-sm sm:text-base md:text-lg lg:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-1">
                {stat.number}
              </div>
              <div className="text-[10px] sm:text-[11px] md:text-xs lg:text-sm text-slate-600 dark:text-slate-300 leading-tight">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </motion.div> */}
      </div>
    </section>
  );
};

export default TestimonialsSlider;
